import React, { useState, useEffect } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { setPageTitle } from '@/utils/pageTitle';
import {
  FolderKanban,
  LayoutDashboard,
  FolderOpen,
  CheckSquare,
  Settings,
  ChevronLeft,
  Home,
  Shield,
  Menu,
  X
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useIsAdmin } from '@/hooks/useIsAdmin';
import { employeeNamesService } from '@/services/employeeNames';
import { UserInfoSection } from '@/components/layout/UserInfoSection';

const ProductManagerLayout: React.FC = () => {
  const [displayName, setDisplayName] = useState<string>('');
  const location = useLocation();
  const navigate = useNavigate();
  const { userEmail } = useAuth();
  const [isCollapsed, setIsCollapsed] = useState(window.innerWidth < 768);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const loadDisplayName = async () => {
      if (userEmail) {
        const name = await employeeNamesService.getNameFromEmail(userEmail);
        if (name) {
          setDisplayName(name);
        }
      }
    };
    loadDisplayName();
  }, [userEmail]);

  // Set page title based on current route
  useEffect(() => {
    const path = location.pathname;
    let title = 'Product Manager';
    
    if (path === '/product-manager') {
      title = 'Product Manager';
    } else if (path.startsWith('/product-manager/projects')) {
      if (path.includes('/projects/') && path.split('/').length > 3) {
        title = 'Project Details | Product Manager';
      } else {
        title = 'Projects | Product Manager';
      }
    } else if (path.startsWith('/product-manager/tasks')) {
      if (path.includes('/tasks/') && path.split('/').length > 3) {
        title = 'Task Details | Product Manager';
      } else {
        title = 'Tasks | Product Manager';
      }
    } else if (path.startsWith('/product-manager/settings')) {
      title = 'Settings | Product Manager';
    }
    
    setPageTitle(title);
  }, [location.pathname]);

  const isActive = (path: string, exact = false) => {
    if (exact) {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  const { isAdmin } = useIsAdmin();

  const handleNavigate = (to: string) => {
    navigate(to);
    setIsMobileMenuOpen(false);
  };

  return (
    <div className="h-screen bg-gray-50 dark:bg-gray-900 flex overflow-hidden product-manager-app">
      {/* Mobile Menu Button */}
      <div className="md:hidden fixed top-0 left-0 right-0 h-16 bg-white dark:bg-gray-800 z-40 border-b dark:border-gray-700 px-4 flex items-center">
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
        >
          {isMobileMenuOpen ? (
            <X className="h-6 w-6 text-gray-600" />
          ) : (
            <Menu className="h-6 w-6 text-gray-600" />
          )}
        </button>
      </div>

      {/* Mobile Menu Backdrop */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-30 md:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          bg-white dark:bg-gray-800 shadow-sm fixed md:relative h-full z-40
          transform transition-transform duration-300 ease-in-out flex flex-col
          ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0 hidden md:flex'}
          ${isCollapsed && !isMobileMenuOpen ? 'w-16 md:w-16' : 'w-64 md:w-64'}
        `}
      >
        {/* Collapse Toggle - Hidden on Mobile */}
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="absolute -right-3 top-6 bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-full p-1 shadow-sm hover:bg-gray-50 dark:hover:bg-gray-700 hidden md:block"
        >
          <ChevronLeft className={`h-4 w-4 text-gray-600 transition-transform duration-300 ${isCollapsed ? 'rotate-180' : ''}`} />
        </button>

        <div
          className="border-b flex justify-center items-center h-46 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700"
          onClick={() => handleNavigate('/product-manager')}
          title="Go to Dashboard"
        >
          {isCollapsed && !isMobileMenuOpen ? (
            <FolderKanban className="h-12 w-12 text-violet-600 mb-1 mt-1 transition-all duration-300" />
          ) : (
            <div className="flex flex-col items-center justify-center py-8">
              <FolderKanban className="h-20 w-20 text-violet-600 mb-2" />
              <h1 className="text-xl font-bold text-gray-900 dark:text-white">Product Manager</h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">Plan & Execute</p>
            </div>
          )}
        </div>
        
        <nav className="flex-1 mt-4 overflow-y-auto flex flex-col">
          {/* Main Navigation */}
          <div
            onClick={() => handleNavigate('/product-manager')}
            className={`flex items-center px-4 py-2 text-sm cursor-pointer ${
              isActive('/product-manager', true) ? 'bg-violet-50 dark:bg-violet-900/50 text-violet-600 dark:text-violet-300' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
            }`}
          >
            <LayoutDashboard className={`${isCollapsed && !isMobileMenuOpen ? '' : 'mr-3'} h-5 w-5`} />
            {(!isCollapsed || isMobileMenuOpen) && 'Dashboard'}
          </div>

          <div
            onClick={() => handleNavigate('/product-manager/projects')}
            className={`flex items-center px-4 py-2 text-sm cursor-pointer ${
              isActive('/product-manager/projects') ? 'bg-violet-50 dark:bg-violet-900/50 text-violet-600 dark:text-violet-300' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
            }`}
          >
            <FolderOpen className={`${isCollapsed && !isMobileMenuOpen ? '' : 'mr-3'} h-5 w-5`} />
            {(!isCollapsed || isMobileMenuOpen) && 'Projects'}
          </div>

          <div
            onClick={() => handleNavigate('/product-manager/tasks')}
            className={`flex items-center px-4 py-2 text-sm cursor-pointer ${
              isActive('/product-manager/tasks') ? 'bg-violet-50 dark:bg-violet-900/50 text-violet-600 dark:text-violet-300' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
            }`}
          >
            <CheckSquare className={`${isCollapsed && !isMobileMenuOpen ? '' : 'mr-3'} h-5 w-5`} />
            {(!isCollapsed || isMobileMenuOpen) && 'Tasks'}
          </div>


          {/* Divider */}
          <div className="mx-4 my-4 border-t border-gray-200 dark:border-gray-700" />

          {/* Utility Navigation */}
          <div
            onClick={() => handleNavigate('/product-manager/settings')}
            className={`flex items-center px-4 py-2 text-sm cursor-pointer ${
              isActive('/product-manager/settings') ? 'bg-violet-50 dark:bg-violet-900/50 text-violet-600 dark:text-violet-300' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
            }`}
          >
            <Settings className={`${isCollapsed && !isMobileMenuOpen ? '' : 'mr-3'} h-5 w-5`} />
            {(!isCollapsed || isMobileMenuOpen) && 'Settings'}
          </div>

          {/* Admin Link - Only shown to admin users */}
          {isAdmin && (
            <div
              onClick={() => handleNavigate('/admin')}
              className={`flex items-center px-4 py-2 text-sm cursor-pointer ${
                isActive('/admin') ? 'bg-violet-50 dark:bg-violet-900/50 text-violet-600 dark:text-violet-300' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
              }`}
            >
              <Shield className={`${isCollapsed && !isMobileMenuOpen ? '' : 'mr-3'} h-5 w-5`} />
              {(!isCollapsed || isMobileMenuOpen) && 'Admin'}
            </div>
          )}

          {/* Apps Hub Link */}
          <div
            onClick={() => handleNavigate('/dashboard')}
            className="flex items-center px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer"
          >
            <Home className={`${isCollapsed && !isMobileMenuOpen ? '' : 'mr-3'} h-5 w-5`} />
            {(!isCollapsed || isMobileMenuOpen) && 'Apps Hub'}
          </div>
        </nav>

        <UserInfoSection
          displayName={displayName}
          isCollapsed={isCollapsed && !isMobileMenuOpen}
        />
      </aside>

      {/* Main Content Area */}
      <main className="flex-1 overflow-auto pt-16 md:pt-0 relative">
        <div className="p-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default ProductManagerLayout;